import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InfoIcon, SpinnerIcon, WarningIcon } from '@phosphor-icons/react';

import FolderBrowser from 'src/features/document-hub/components/FolderBrowser';
import { isInOrganisationLibrary } from 'src/features/document-hub/components/DocumentMoveModal';
import Button from 'src/components/ui/Button';
import Banner from 'src/components/ui/Banner';
import Modal from 'src/components/ui/Modal';
import {
  DocumentHubNode,
  isGroupOwnedNode,
  isWorkflowDocumentType,
  workflowMoveWarning
} from 'src/features/document-hub/types';

export interface DocumentPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the selected document ids when the user confirms. Nodes are the hub rows for those ids, when the picker has seen them. */
  onConfirm: (documentIds: string[], nodes?: DocumentHubNode[]) => void;
  title?: string;
  confirmLabel?: string;
  isLoading?: boolean;
  /** When false, the file list is not shown and confirm is disabled. */
  contentsReady?: boolean;
  contentsError?: string | null;
  onRetryContents?: () => void;
  /** Hide these document ids from the picker list. */
  excludeDocumentIds?: string[];
  /** Show these document ids greyed-out and unselectable (e.g. already in the destination group). */
  disabledDocumentIds?: string[];
  disabledDocumentReason?: string;
  /**
   * Document ids to treat as already-selected when the modal opens.
   * These are included in the ids passed to `onConfirm` even if the user
   * doesn't interact with the picker (useful for edit flows).
   * Note: the FolderBrowser table does not visually pre-check these rows —
   * any interaction with the table resets the effective selection to whatever
   * the table reports.
   */
  initialSelectedIds?: string[];
  /** Restrict browsing to this hub folder (Home/Back stop here). */
  rootFolderId?: string | null;
  rootFolderLabel?: string;
  /** When true, omit subfolders and list files in the root folder only. */
  filesOnly?: boolean;
  /** Restrict listed files to these document types. */
  allowedDocumentTypes?: Set<string>;
  /** When set, only these folder types are listed as navigable rows. */
  allowedFolderTypes?: Set<string>;
  /** At hub root, only these folders are shown (e.g. this group + Organisation). */
  visibleRootFolderIds?: readonly string[];
  /** Hide the Drafts folder and canvas files. */
  omitDrafts?: boolean;
  /**
   * When set, selected organisation-library / group-owned files show the same
   * access-loss warning as the single-file Move modal.
   */
  moveAccessWarning?: { tenantName?: string; groupName?: string };
  /** When false, only one document can be selected. Defaults to true. */
  multiselect?: boolean;
}

export default function DocumentPickerModal({
  open,
  onClose,
  onConfirm,
  title = 'Link documents',
  confirmLabel = 'Link',
  isLoading = false,
  contentsReady = true,
  contentsError = null,
  onRetryContents,
  excludeDocumentIds = [],
  disabledDocumentIds = [],
  disabledDocumentReason,
  initialSelectedIds = [],
  rootFolderId = null,
  rootFolderLabel,
  filesOnly = false,
  allowedDocumentTypes,
  allowedFolderTypes,
  visibleRootFolderIds,
  omitDrafts = false,
  moveAccessWarning,
  multiselect = true
}: DocumentPickerModalProps) {
  const excludeSet = useMemo(() => new Set(excludeDocumentIds), [excludeDocumentIds]);
  const disabledSet = useMemo(() => new Set(disabledDocumentIds), [disabledDocumentIds]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<DocumentHubNode[]>([]);
  const selectedNodesByIdRef = useRef<Map<string, DocumentHubNode>>(new Map());
  const [openFolderId, setOpenFolderId] = useState<string | null>(rootFolderId);
  const [breadcrumb, setBreadcrumb] = useState<DocumentHubNode[]>([]);
  // Increment on each open to remount FolderBrowser and reset its internal state.
  const [sessionKey, setSessionKey] = useState(0);
  const restrictToRoot = filesOnly;

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelectedIds(initialSelectedIds);
      setSelectedNodes([]);
      selectedNodesByIdRef.current = new Map();
      setOpenFolderId(rootFolderId);
      setBreadcrumb([]);
      setSessionKey((k) => k + 1);
    }
    prevOpenRef.current = open;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearSelection = useCallback(() => {
    selectedNodesByIdRef.current = new Map();
    setSelectedIds([]);
    setSelectedNodes([]);
  }, []);

  const handleOpenFolder = useCallback(
    (node: DocumentHubNode) => {
      clearSelection();
      setOpenFolderId(node.id);
      setBreadcrumb((crumbs) => [...crumbs, node]);
    },
    [clearSelection]
  );

  const handleNavigateToFolder = useCallback(
    (folderId: string | null) => {
      clearSelection();
      if (folderId == null || folderId === rootFolderId) {
        setOpenFolderId(rootFolderId);
        setBreadcrumb([]);
        return;
      }
      setOpenFolderId(folderId);
      setBreadcrumb((crumbs) => {
        const index = crumbs.findIndex((crumb) => crumb.id === folderId);
        return index >= 0 ? crumbs.slice(0, index + 1) : crumbs;
      });
    },
    [clearSelection, rootFolderId]
  );

  const handleSelectionChange = useCallback((ids: string[], nodes: DocumentHubNode[] = []) => {
    const next = new Map(selectedNodesByIdRef.current);
    for (const node of nodes) {
      next.set(node.document_id ?? node.id, node);
    }
    for (const id of [...next.keys()]) {
      if (!ids.includes(id)) next.delete(id);
    }
    selectedNodesByIdRef.current = next;
    setSelectedIds(ids);
    setSelectedNodes(
      ids.flatMap((id) => {
        const node = next.get(id);
        return node ? [node] : [];
      })
    );
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(
      selectedIds,
      selectedIds.flatMap((id) => {
        const node = selectedNodesByIdRef.current.get(id);
        return node ? [node] : [];
      })
    );
  }, [onConfirm, selectedIds]);

  const workflowWarning = useMemo(() => {
    if (!moveAccessWarning || selectedIds.length === 0) return null;
    const workflowTypes = selectedNodes
      .map((node) => node.document_type)
      .filter((documentType) => isWorkflowDocumentType(documentType));
    return workflowTypes.length > 0 ? workflowMoveWarning(workflowTypes) : null;
  }, [moveAccessWarning, selectedIds.length, selectedNodes]);

  const accessWarning = useMemo(() => {
    if (!moveAccessWarning || selectedIds.length === 0) return null;
    const orgCount = selectedNodes.filter(isInOrganisationLibrary).length;
    const groupCount = selectedNodes.filter((node) => isGroupOwnedNode(node)).length;
    if (orgCount === 0 && groupCount === 0) return null;
    const tenant = moveAccessWarning.tenantName?.trim() || 'your organisation';
    const destGroup = moveAccessWarning.groupName?.trim() || 'this group';
    const sourceGroupNames = [
      ...new Set(
        selectedNodes
          .filter((node) => isGroupOwnedNode(node))
          .map((node) => {
            const match = node.subtitle?.match(/^(?:Group Folders|Groups)\s*[›>]\s*(\S.*)?$/);
            return match?.[1]?.trim();
          })
          .filter((name): name is string => !!name && name !== destGroup)
      )
    ];
    const previousGroup =
      sourceGroupNames.length === 1
        ? sourceGroupNames[0]
        : sourceGroupNames.length > 1
          ? 'the previous groups'
          : 'the previous group';
    const affected = orgCount + groupCount;
    const thisOrThese = affected === 1 ? 'This file' : 'These files';
    if (orgCount > 0 && groupCount > 0) {
      return `${thisOrThese} will leave the Organisation Folder or ${previousGroup}. People who currently have access will lose it unless they are members of ${destGroup}.`;
    }
    if (orgCount > 0) {
      return `Members of ${tenant} who are not in ${destGroup} will lose access.`;
    }
    return `Members of ${previousGroup} who are not in ${destGroup} will lose access.`;
  }, [moveAccessWarning, selectedIds.length, selectedNodes]);

  const ctaLabel =
    !multiselect || selectedIds.length === 0
      ? confirmLabel
      : `${confirmLabel} ${selectedIds.length} document${selectedIds.length !== 1 ? 's' : ''}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<span className="text-lg font-semibold">{title}</span>}
      className="w-full max-w-6xl max-h-[95vh]"
      disableBackdropClick
      overflowHidden
    >
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!contentsReady && !contentsError ? (
          <div className="flex items-center justify-center py-16 text-sm text-content-subtlest gap-2">
            <SpinnerIcon size={16} className="animate-spin" />
            Loading…
          </div>
        ) : contentsError ? (
          <div className="flex flex-col items-center gap-3 py-16 px-6 text-center">
            <p className="text-sm text-content-subtle">{contentsError}</p>
            {onRetryContents && (
              <Button variant="outline" size="md" onClick={onRetryContents}>
                Try again
              </Button>
            )}
          </div>
        ) : (
          <FolderBrowser
            key={sessionKey}
            openFolderId={openFolderId}
            breadcrumb={breadcrumb}
            onOpenFolder={restrictToRoot ? () => {} : handleOpenFolder}
            onNavigateToFolder={handleNavigateToFolder}
            canWrite={false}
            viewMode="folders"
            rootFolderId={rootFolderId}
            rootFolderLabel={rootFolderLabel}
            filesOnly={restrictToRoot}
            allowedDocumentTypes={allowedDocumentTypes}
            allowedFolderTypes={allowedFolderTypes}
            visibleRootFolderIds={visibleRootFolderIds}
            omitDrafts={omitDrafts}
            multiselect
            enableMultiRowSelection={multiselect}
            pickerMode
            excludeDocumentIds={excludeSet}
            disabledDocumentIds={disabledSet}
            disabledDocumentReason={disabledDocumentReason}
            onSelectionChange={handleSelectionChange}
          />
        )}
      </div>
      <div className="flex flex-col gap-3 pt-3 flex-shrink-0">
        {workflowWarning && (
          <Banner variant="info" icon={InfoIcon}>
            {workflowWarning}
          </Banner>
        )}
        {accessWarning && (
          <Banner variant="warning" icon={WarningIcon}>
            {accessWarning}
          </Banner>
        )}
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleConfirm}
            disabled={selectedIds.length === 0 || isLoading || !contentsReady || !!contentsError}
            loading={isLoading}
          >
            {ctaLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
